// D62 — Page /engins : tableau types + grille engins
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enginsApi, typeEnginsApi } from "@/lib/api";
import type { Engin, TypeEngin } from "@/types/engin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Link } from "react-router-dom";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { EngineIcon, ENGINE_ICON_NAMES, getEngineIcon } from "@/components/engins/engine-icon";
import { useT } from "@/lib/i18n";

// Valeur brute du statut stockée en DB (FR) — la comparaison se fait sur
// cette valeur, l'AFFICHAGE est traduit via t("engins.statut.active").
const STATUT_ACTIF = "Actif";
// Les statuts sont stockés en dur en FR en base : on traduit à l'affichage.
const t_ = (s: string) => (s === STATUT_ACTIF ? "Actif" : "Inactif");
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

export default function ListeEnginsPage() {
  const t = useT();
  const canWrite = useCan("write");
  const [showCreateType, setShowCreateType] = useState(false);
  const [showCreateEngin, setShowCreateEngin] = useState(false);
  const [deleteType, setDeleteType] = useState<{ id: number; name: string } | null>(null);
  const [deleteEngin, setDeleteEngin] = useState<{ id: number; code: string } | null>(null);
  // P57-D : édition (le backend expose déjà PATCH /engins/{id} et
  // PATCH /type-engins/{id} — c'était un manque frontend pur).
  const [editEngin, setEditEngin] = useState<Engin | null>(null);
  const [editType, setEditType] = useState<TypeEngin | null>(null);
  const { toast } = useToast();
  const qc = useQueryClient();

  const updateEnginMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<Engin> }) =>
      enginsApi.update(payload.id, payload.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engins"] });
      toast({ title: ht("Engin modifié"), variant: "success" });
      setEditEngin(null);
    },
    onError: (err) => {
      toast({ title: ht("Erreur modification"), description: (err as Error).message, variant: "error" });
    },
  });

  const updateTypeMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<TypeEngin> }) =>
      typeEnginsApi.update(payload.id, {
        name: payload.data.name,
        statut: payload.data.statut,
        icone: payload.data.icone ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["type-engins"] });
      qc.invalidateQueries({ queryKey: ["engins"] });
      toast({ title: ht("Type modifié"), variant: "success" });
      setEditType(null);
    },
    onError: (err) => {
      toast({ title: ht("Erreur modification"), description: (err as Error).message, variant: "error" });
    },
  });

  const setdeleteEnginSafe = (eng: Engin) =>
    setDeleteEngin({ id: eng.id, code: eng.code });

  const deleteTypeMutation = useMutation({
    mutationFn: (id: number) => typeEnginsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["type-engins"] });
      qc.invalidateQueries({ queryKey: ["engins"] });
      toast({ title: ht("Type supprimé"), variant: "success" });
      setDeleteType(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({ title, description: msg, variant: "error", duration: 8000 });
    },
  });

  const deleteEnginMutation = useMutation({
    mutationFn: (id: number) => enginsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engins"] });
      toast({ title: ht("Engin supprimé"), variant: "success" });
      setDeleteEngin(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith("Impossible de supprimer") ? ht("Suppression impossible") : "Erreur suppression";
      toast({ title, description: msg, variant: "error", duration: 8000 });
    },
  });

  const { data: types } = useQuery({
    queryKey: ["type-engins"],
    queryFn: () => typeEnginsApi.list(),
  });
  const { data: engins } = useQuery({
    queryKey: ["engins"],
    queryFn: () => enginsApi.list(),
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("engins.title")}</h1>
          <p className="text-muted-foreground">
            {t("engins.subtitle_prefix")} ({engins?.length ?? 0} {t("engins.engin_plural")}, {types?.length ?? 0} {t("engins.types_title_short")})
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCreateType(true)}>
              + {t("engins.new_type")}
            </Button>
            <Button onClick={() => setShowCreateEngin(true)}>+ {t("engins.new_engin")}</Button>
          </div>
        )}
      </div>

      {/* Types */}
      <Card>
        <CardHeader>
          <CardTitle>{t("engins.types_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {types?.map((te) => (
              <div
                key={te.id}
                data-testid="type-row"
                className="group relative rounded-md border border-border p-3 hover:bg-muted/30"
              >
                <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  {canWrite && (
                    <button
                      data-testid="type-edit"
                      onClick={() => setEditType(te)}
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      title={ht("Modifier ce type d'engin")}
                      aria-label={ht("Modifier ce type d'engin")}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {canWrite && (
                    <button
                      onClick={() => setDeleteType({ id: te.id, name: te.name })}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                      title={t("engins.delete.title_type")}
                      aria-label={ht("Supprimer ce type d'engin")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {/* P57-C : l'icône était stockée mais jamais relue */}
                <EngineIcon
                  name={te.icone}
                  className="mb-1 h-7 w-7 text-primary"
                  title
                />
                <div className="font-mono text-xs text-muted-foreground">#{te.id}</div>
                <div className="font-semibold">{te.name}</div>
                <div className="mt-1">
                  <span
                    className={
                      te.statut === STATUT_ACTIF
                        ? "rounded bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300"
                        : "rounded bg-destructive/15 border border-destructive/40 px-2 py-0.5 text-xs text-destructive dark:text-red-300"
                    }
                  >
                    {te.statut === STATUT_ACTIF ? t("engins.statut.active") : t("engins.statut.inactive")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Engins */}
      <Card>
        <CardHeader>
          <CardTitle>{t("engins.engins_title")} ({engins?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">{t("engins.col.code")}</th>
                <th className="px-3 py-2">{t("engins.col.immat")}</th>
                <th className="px-3 py-2">{t("engins.col.chassis")}</th>
                <th className="px-3 py-2">{t("engins.col.type")}</th>
                <th className="px-3 py-2">{t("engins.col.site")}</th>
                <th className="px-3 py-2 text-right">{t("engins.col.km")}</th>
                <th className="px-3 py-2 text-right">{t("engins.col.hours")}</th>
                <th className="px-3 py-2">{t("engins.col.active")}</th>
              </tr>
            </thead>
            <tbody>
              {engins?.map((eng) => {
                const engType = types?.find((x) => x.id === eng.type_engin_id);
                return (
                <tr key={eng.id} data-testid="engin-row" className="group border-b hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono font-semibold">
                    <div className="flex items-center gap-1">
                      <span data-testid="engin-code">{eng.code}</span>
                      <span className="ml-auto flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Link
                          to={`/engins/${eng.id}`}
                          className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          title={ht("Voir cet engin")}
                          aria-label={ht("Voir cet engin")}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Link>
                        {canWrite && (
                          <button
                            data-testid="engin-edit"
                            onClick={() => setEditEngin(eng)}
                            className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            title={ht("Modifier cet engin")}
                            aria-label={ht("Modifier cet engin")}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canWrite && (
                          <button
                            onClick={() => setdeleteEnginSafe(eng)}
                            className="rounded p-0.5 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                            title={t("engins.delete.title_engin")}
                            aria-label={ht("Supprimer cet engin")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{eng.immatriculation ?? "-"}</td>
                  <td className="px-3 py-2 text-xs">{eng.n_chassis ?? "-"}</td>
                  {/* P57-C : on affichait « #id », sans nom ni icône */}
                  <td className="px-3 py-2">
                    {engType ? (
                      <span className="inline-flex items-center gap-1.5">
                        <EngineIcon name={engType.icone} className="h-4 w-4 text-primary" title />
                        {engType.name}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{eng.site_id ? `#${eng.site_id}` : "-"}</td>
                  <td className="px-3 py-2 text-right">{eng.kilometrage.toLocaleString("fr-FR")} km</td>
                  <td className="px-3 py-2 text-right">{eng.compteur_horaire} h</td>
                  <td className="px-3 py-2">
                    {eng.is_active ? (
                      <span className="rounded bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">{t("engins.active.yes")}</span>
                    ) : (
                      <span className="rounded bg-destructive/15 border border-destructive/40 px-2 py-0.5 text-xs text-destructive dark:text-red-300">{t("engins.active.no")}</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table></div>
        </CardContent>
      </Card>

      {/* P57-D : modales d'édition (PATCH /engins/{id}, /type-engins/{id}) */}
      {editType && (
        <EditTypeModal
          type={editType}
          onClose={() => setEditType(null)}
          pending={updateTypeMutation.isPending}
          onSave={(data) => updateTypeMutation.mutate({ id: editType.id, data })}
        />
      )}
      {editEngin && (
        <EditEnginModal
          engin={editEngin}
          types={types ?? []}
          onClose={() => setEditEngin(null)}
          pending={updateEnginMutation.isPending}
          onSave={(data) => updateEnginMutation.mutate({ id: editEngin.id, data })}
        />
      )}

      {showCreateType && <CreateTypeModal onClose={() => setShowCreateType(false)} />}
      {showCreateEngin && (
        <CreateEnginModal
          onClose={() => setShowCreateEngin(false)}
          types={types ?? []}
          enginIdDefault={null}
        />
      )}

      <ConfirmDialog
        open={deleteType !== null}
        title={ht("Supprimer ce type d'engin ?")}
        description={`Cette action supprimera définitivement le type "${deleteType?.name ?? ''}" et tous les engins associés. Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        onConfirm={() => deleteType && deleteTypeMutation.mutate(deleteType.id)}
        onCancel={() => setDeleteType(null)}
        loading={deleteTypeMutation.isPending}
      />

      <ConfirmDialog
        open={deleteEngin !== null}
        title={ht("Supprimer cet engin ?")}
        description={`Cette action supprimera définitivement l'engin "${deleteEngin?.code ?? ''}". Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        onConfirm={() => deleteEngin && deleteEnginMutation.mutate(deleteEngin.id)}
        onCancel={() => setDeleteEngin(null)}
        loading={deleteEnginMutation.isPending}
      />
    </div>
  );
}

// ─── D63 — Modale création type d'engin ────────────────────────────────

/** P57-D — Modale d'édition d'un type d'engin (nom, statut, icône). */
function EditTypeModal({
  type,
  onClose,
  onSave,
  pending,
}: {
  type: TypeEngin;
  onClose: () => void;
  onSave: (data: Partial<TypeEngin>) => void;
  pending: boolean;
}) {
  const [name, setName] = useState(type.name);
  const [icone, setIcone] = useState(type.icone ?? "Truck");
  const [statut, setStatut] = useState(type.statut);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card data-testid="edit-type-modal" className="w-[min(28rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{ht("Modifier le type d'engin")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs">{ht("Nom")}</label>
              <input
                name="name"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Statut")}</label>
              <select
                name="statut"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={statut}
                onChange={(e) => setStatut(e.target.value as TypeEngin["statut"])}
              >
                <option value={STATUT_ACTIF}>{t_(STATUT_ACTIF)}</option>
                <option value="Inactif">{t_("Inactif")}</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Icône")}</label>
              <IconPicker value={icone} onChange={setIcone} />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{ht("Annuler")}</Button>
            <Button
              onClick={() => onSave({ name, statut, icone })}
              disabled={pending || !name.trim()}
            >
              {ht("Enregistrer")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** P57-D — Modale d'édition d'un engin (champs UpdateEnginSchema). */
function EditEnginModal({
  engin,
  types,
  onClose,
  onSave,
  pending,
}: {
  engin: Engin;
  types: TypeEngin[];
  onClose: () => void;
  onSave: (data: Partial<Engin>) => void;
  pending: boolean;
}) {
  const [form, setForm] = useState<Partial<Engin>>({ ...engin });

  const num = (key: keyof Engin, label: string, step = 1) => (
    <div>
      <label className="mb-1 block text-xs">{label}</label>
      <input
        type="number"
        step={step}
        min={0}
        name={String(key)}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        value={(form[key] as number | null) ?? ""}
        onChange={(e) =>
          setForm({ ...form, [key]: e.target.value === "" ? null : Number(e.target.value) })
        }
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4">
      <Card data-testid="edit-engin-modal" className="my-8 w-[min(46rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>
            {ht("Modifier l'engin")} — {engin.code}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs">{ht("Code")} *</label>
              <input
                name="code"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.code ?? ""}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Immatriculation")}</label>
              <input
                name="immatriculation"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.immatriculation ?? ""}
                onChange={(e) => setForm({ ...form, immatriculation: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("N° parc")}</label>
              <input
                name="n_parc"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.n_parc ?? ""}
                onChange={(e) => setForm({ ...form, n_parc: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("N° châssis")}</label>
              <input
                name="n_chassis"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.n_chassis ?? ""}
                onChange={(e) => setForm({ ...form, n_chassis: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Type")}</label>
              <select
                name="type_engin_id"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.type_engin_id ?? ""}
                onChange={(e) =>
                  setForm({ ...form, type_engin_id: Number(e.target.value) })
                }
              >
                <option value="">{ht("— Aucun —")}</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Site")} (id)</label>
              <input
                type="number"
                name="site_id"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.site_id ?? ""}
                onChange={(e) =>
                  setForm({ ...form, site_id: e.target.value === "" ? null : Number(e.target.value) })
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs">{ht("Contrat")} (id)</label>
              <input
                type="number"
                name="contrat_id"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.contrat_id ?? ""}
                onChange={(e) =>
                  setForm({ ...form, contrat_id: e.target.value === "" ? null : Number(e.target.value) })
                }
              />
            </div>
            {num("kilometrage", ht("Kilométrage (km)"))}
            {num("compteur_horaire", ht("Compteur horaire (h)"))}
            {num("huile_moteur", ht("Huile moteur (%)"))}
            {num("huile_boite", ht("Huile boîte (%)"))}
            {num("eau_radiateur", ht("Eau radiateur (%)"))}
            {num("graissage", ht("Graissage (%)"))}
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active ?? true}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                {ht("Actif")}
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{ht("Annuler")}</Button>
            <Button
              onClick={() => onSave(form)}
              disabled={pending || !form.code?.trim()}
            >
              {ht("Enregistrer")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * P57-C — Sélecteur d'icône sous forme de GRILLE de glyphes.
 * Remplace le <select> natif qui affichait des noms de classes (Truck,
 * Crane…) sans aucun rendu visuel.
 */
function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string) => void;
}) {
  return (
    <div>
      <div
        data-testid="icon-picker"
        className="grid max-h-56 grid-cols-6 gap-1 overflow-y-auto rounded-md border border-input bg-background p-2"
      >
        {ENGINE_ICON_NAMES.map((name) => {
          const Icon = getEngineIcon(name);
          const active = value === name;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onChange(name)}
              title={name}
              aria-label={name}
              aria-pressed={active}
              className={`flex h-9 items-center justify-center rounded transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {ht("Sélectionnée")} : <span className="font-mono">{value}</span>
      </p>
    </div>
  );
}

function CreateTypeModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [icone, setIcone] = useState("Truck");

  const create = useMutation({
    mutationFn: () =>
      fetch(`${API_BASE}/v1/type-engins`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, icone }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || "Erreur");
        return r.json();
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["type-engins"] });
      onClose();
      toast({ title: 'Type créé', description: created.name, variant: 'success' });
    },
    onError: (err) => {
      toast({ title: 'Erreur création type', description: (err as Error).message, variant: 'error' });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(28rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{t("engins.new_type_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs">{ht("Nom")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={ht("Ex: BULLDOZER")}
              />
            </div>
            <div>
              <label className="text-xs">
                {ht("Icône")} ({ENGINE_ICON_NAMES.length} {ht("disponibles")})
              </label>
              <IconPicker value={icone} onChange={setIcone} />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{ht("Annuler")}
            </Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !name}>
              {create.isPending ? "Création..." : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── D64 — Modale engin 3 étapes ────────────────────────────────────────
function CreateEnginModal({
  onClose,
  types,
}: {
  onClose: () => void;
  types: import("@/types/engin").TypeEngin[];
  enginIdDefault: number | null;
}) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  // qc géré dans les modales
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    code: "",
    immatriculation: "",
    n_parc: "",
    n_chassis: "",
    type_engin_id: types[0]?.id ?? 0,
    kilometrage: 0,
    compteur_horaire: 0,
    huile_moteur: 100,
    huile_boite: 100,
    eau_radiateur: 100,
    graissage: 100,
  });

  const create = useMutation({
    mutationFn: () =>
      fetch(`${API_BASE}/v1/engins`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || "Erreur");
        return r.json();
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["engins"] });
      onClose();
      toast({ title: 'Engin créé', description: created.code, variant: 'success' });
    },
    onError: (err) => {
      toast({ title: 'Erreur création engin', description: (err as Error).message, variant: 'error' });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(36rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{t("engins.new_engin")} — Étape {step}/3</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Stepper */}
          <div className="mb-4 flex gap-2">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`flex-1 rounded px-3 py-1.5 text-xs ${
                  step === s
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {s === 1 ? "Identification" : s === 2 ? "Affectation" : "Indicateurs initiaux"}
              </button>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-2">
              <div>
                <label className="text-xs">{ht("Code (unique) *")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={ht("ENG-XXX")}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs">{ht("Immatriculation")}</label>
                  <input
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.immatriculation}
                    onChange={(e) => setForm({ ...form, immatriculation: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("N° parc")}</label>
                  <input
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.n_parc}
                    onChange={(e) => setForm({ ...form, n_parc: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs">{ht("N° châssis")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.n_chassis}
                  onChange={(e) => setForm({ ...form, n_chassis: e.target.value })}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <div>
                <label className="text-xs">{ht("Type d'engin *")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.type_engin_id}
                  onChange={(e) => setForm({ ...form, type_engin_id: Number(e.target.value) })}
                >
                  {types.map((te) => (
                    <option key={te.id} value={te.id}>
                      {te.name} (
                      {te.statut === STATUT_ACTIF ? t("engins.statut.active") : t("engins.statut.inactive")}
                      )
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Compteurs initiaux (modifiables plus tard)
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs">{ht("Kilométrage")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.kilometrage}
                    onChange={(e) => setForm({ ...form, kilometrage: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("Compteur horaire")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.compteur_horaire}
                    onChange={(e) => setForm({ ...form, compteur_horaire: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("Huile moteur (%)")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.huile_moteur}
                    onChange={(e) => setForm({ ...form, huile_moteur: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("Huile boîte (%)")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.huile_boite}
                    onChange={(e) => setForm({ ...form, huile_boite: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("Eau radiateur (%)")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.eau_radiateur}
                    onChange={(e) => setForm({ ...form, eau_radiateur: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs">{ht("Graissage (%)")}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.graissage}
                    onChange={(e) => setForm({ ...form, graissage: Number(e.target.value) })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            >
              {step === 1 ? "Annuler" : "← Étape précédente"}
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !form.code}
              >{ht("Étape suivante →")}
              </Button>
            ) : (
              <Button
                onClick={() => create.mutate()}
                disabled={create.isPending || !form.code || !form.type_engin_id}
              >
                {create.isPending ? "Création..." : "Créer"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
